import { useEffect, useMemo, useReducer, useRef } from "react"
import { createPendingQueue, type PendingItem, type RejectedItem, type SendOutcome } from "./pending-queue"

/**
 * Rede fraca no armazém (Fase 2c, L4; correção 9): **uma leitura nunca se
 * perde.** O hook liga a fila (`pending-queue.ts`) ao ecrã e repete em
 * silêncio a cada 5 s, ao voltar a rede e ao reabrir o ecrã. As recusas ficam
 * em `rejected` («Não gravadas») com «Tentar de novo» e «Descartar».
 */

export type { PendingItem, RejectedItem, SendOutcome }
export { isRetryable } from "./pending-queue"

export function usePendingQueue<P, R>(
  storageKey: string,
  send: (payload: P) => Promise<R>,
  onSent?: (result: R, payload: P) => void,
  opts: { serial?: boolean } = {},
) {
  const [, rerender] = useReducer((n: number) => n + 1, 0)
  const sendRef = useRef(send)
  sendRef.current = send
  const onSentRef = useRef(onSent)
  onSentRef.current = onSent

  const queue = useMemo(
    () =>
      createPendingQueue<P, R>({
        storageKey,
        send: (p) => sendRef.current(p),
        onSent: (r, p) => onSentRef.current?.(r, p),
        onChange: rerender,
        serial: opts.serial,
      }),
    [storageKey, opts.serial],
  )

  useEffect(() => {
    queue.flush()
    const timer = setInterval(() => queue.items().length > 0 && queue.flush(), 5000)
    const online = () => queue.flush()
    window.addEventListener("online", online)
    return () => {
      clearInterval(timer)
      window.removeEventListener("online", online)
    }
  }, [queue])

  return {
    items: queue.items(),
    rejected: queue.rejected(),
    enqueue: queue.enqueue,
    flush: queue.flush,
    retryRejected: queue.retryRejected,
    discardRejected: queue.discardRejected,
  }
}
